import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { Grid } from "@mui/material";
import { CheckCircleOutline, LocalAtm, Star, Timer, WorkOutline } from "@mui/icons-material";

import { PilotStatisticsTypes } from "../types/crew-dashboard";
import { EnhancedMetricCard } from "../../dashboard/components/admin-widgets/AdminDashboard";
import { PilotProps } from "../types/view-pilot";
import apiService from "../../../service/Api";
import { IAssignmentProps } from "../types/pilot-assignments";
import { IStream } from "../../stream-management/types/view-stream";

interface StatisticsComponentProps {
  pilot: PilotProps | null;
};

export const PilotStatistics: React.FC<StatisticsComponentProps> = ({ pilot }) => {
  const [completedAssignments, setCompletedAssignments] = useState<number>(0);
  const [pilotStats, setPilotStats] = useState<PilotStatisticsTypes>({
    totalEarnings: 0,
    assignments: 0,
    averageRating: 0,
    compliance: 0,
    punctuality: 0,
  });
  
  const fetchPilotAssignmentsCount = useCallback(async () => {
    if (!pilot) return;
    try {
      const response = await apiService.get<{
        count: number;
        next: string | null;
        previous: string | null;
        results: IAssignmentProps[];
      }>("/api/call-sheets/callsheet/",
        { crew_member: pilot.id });

      const completed = await apiService.get<{
        count: number;
        next: string | null;
        previous: string | null;
        results: IStream[];
      }>("/api/streams/stream/fullfilled-with-user/",
        { user_id: pilot.user_id });

      setCompletedAssignments(completed.data.count);
      setPilotStats((prev) => ({
        ...prev,
        assignments: response.data.count
      }));
    } catch (error) {
      console.log({ error }, 'fetchAssignments error');
    }
  }, [pilot]);

  useEffect(() => {
    if (pilot) {
      fetchPilotAssignmentsCount();
      setPilotStats((prev) => ({
        ...prev,
        totalEarnings: 1536,
        averageRating: pilot.average_rating || 0,
        compliance: 73,
        punctuality: 94.7
      }));
    }
  }, [fetchPilotAssignmentsCount, pilot]);

  if (!pilot) return null;

  return (      
    <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <EnhancedMetricCard
            title="Total Earnings"
            value={pilotStats.totalEarnings}
            icon={LocalAtm}
            color="#757575"
            trend={12.9}
            subtitle="This Month"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <EnhancedMetricCard
            title="Assignments"
            value={pilotStats.assignments}
            icon={WorkOutline}
            color="#0000FF"
            trend={53.1}
            subtitle={`${completedAssignments} Completed`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <EnhancedMetricCard
            title="Average Rating"
            value={pilot.average_rating || pilotStats.averageRating}
            icon={Star}
            color="#F79E1B"
            trend={87.4}
            subtitle={`${pilot.rating_count} Reviews`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <EnhancedMetricCard
            title="Compliance"
            value={pilotStats.compliance}
            icon={CheckCircleOutline}
            color="#757575"
            trend={-13.2}
            subtitle="Pending Crew Payouts"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <EnhancedMetricCard
            title="Punctuality"
            value={pilotStats.punctuality}
            icon={Timer}
            color="#757575"
            trend={36.8}
            subtitle="On Time Arrival"
          />
        </Grid>
    </Grid>
  );
};
